/** What a stretch of an Entrez (PubMed / PMC) query is, for display. */
export type PubmedQueryTokenKind =
  /** Whitespace between tokens. */
  | "whitespace"
  /** A Boolean operator: AND, OR or NOT. Entrez only reads them in upper case. */
  | "operator"
  /** A field tag, brackets included: `[Title]`, `[MeSH Terms]`, `[pt]`. */
  | "fieldTag"
  /** A quoted phrase, quotation marks included: `"randomized controlled trial"`. */
  | "phrase"
  /** A grouping parenthesis. */
  | "parenthesis"
  /** The colon of a range: `2020:2024[dp]`. */
  | "range"
  /** Anything else: a search term. */
  | "term";

export interface PubmedQueryToken {
  kind: PubmedQueryTokenKind;
  /** The characters this token covers, exactly as written. */
  text: string;
  /** Set when the token is malformed — a quotation mark or `[` never closed, or a parenthesis with
   *  no partner — so a display can flag it. */
  unmatched?: true;
}

const OPERATORS = new Set(["AND", "OR", "NOT"]);
/** Characters that end a plain term. */
const TERM_END = /[\s"[()\]:]/;

/**
 * Splits an Entrez query into tokens for syntax colouring. Lossless — joining every token's `text`
 * gives the query back — and forgiving: a half-typed or malformed query still tokenizes, with the
 * broken token marked `unmatched`, because the person looking at it is usually in the middle of
 * writing it. It does not judge validity; NCBI is lenient and so is this.
 */
export function tokenizePubmedQuery(query: string): PubmedQueryToken[] {
  const tokens: PubmedQueryToken[] = [];
  const openParentheses: PubmedQueryToken[] = [];
  let i = 0;

  while (i < query.length) {
    const char = query[i];

    if (/\s/.test(char)) {
      let end = i + 1;
      while (end < query.length && /\s/.test(query[end])) end++;
      tokens.push({ kind: "whitespace", text: query.slice(i, end) });
      i = end;
    } else if (char === '"' || char === "[") {
      const closer = char === '"' ? '"' : "]";
      const close = query.indexOf(closer, i + 1);
      const end = close === -1 ? query.length : close + 1;
      tokens.push({
        kind: char === '"' ? "phrase" : "fieldTag",
        text: query.slice(i, end),
        ...(close === -1 ? { unmatched: true as const } : {}),
      });
      i = end;
    } else if (char === "(") {
      const token: PubmedQueryToken = { kind: "parenthesis", text: char };
      openParentheses.push(token);
      tokens.push(token);
      i++;
    } else if (char === ")") {
      const token: PubmedQueryToken = { kind: "parenthesis", text: char };
      if (openParentheses.pop() === undefined) token.unmatched = true;
      tokens.push(token);
      i++;
    } else if (char === ":") {
      tokens.push({ kind: "range", text: char });
      i++;
    } else if (char === "]") {
      tokens.push({ kind: "term", text: char, unmatched: true });
      i++;
    } else {
      let end = i + 1;
      while (end < query.length && !TERM_END.test(query[end])) end++;
      const text = query.slice(i, end);
      tokens.push({ kind: OPERATORS.has(text) ? "operator" : "term", text });
      i = end;
    }
  }

  for (const open of openParentheses) open.unmatched = true;
  return tokens;
}
